import React from "react";
import { Box, Typography, Container, LinearProgress } from "@mui/material";
import { CloudQueue } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useAtom } from "jotai";
import { themeAtom } from "../../-Atoms";

const LoadingState = ({ message = "Loading files..." }) => {
  const [theme] = useAtom(themeAtom);
  const isDarkMode = theme === "dark";

  const panelColors = {
    background: isDarkMode
      ? "linear-gradient(145deg, rgba(25,118,210,0.08), rgba(13,71,161,0.15))"
      : "linear-gradient(145deg, rgba(33,150,243,0.08), rgba(144,202,249,0.25))",
    border: isDarkMode
      ? "1px solid rgba(144, 202, 249, 0.2)"
      : "1px solid rgba(21, 101, 192, 0.2)",
    textPrimary: isDarkMode ? "#e3f2fd" : "#0d47a1",
    textSecondary: isDarkMode
      ? "rgba(227, 242, 253, 0.85)"
      : "rgba(13, 71, 161, 0.75)",
    barBackground: isDarkMode
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(13, 71, 161, 0.08)",
    barFill: isDarkMode ? "#64b5f6" : "#1565c0",
    ring: isDarkMode
      ? "rgba(144, 202, 249, 0.35)"
      : "rgba(21, 101, 192, 0.25)",
    iconBg: isDarkMode
      ? "rgba(13, 71, 161, 0.25)"
      : "rgba(227, 242, 253, 0.9)",
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 12,
      }}
    >
      <Box
        sx={{
          width: "100%",
          borderRadius: 4,
          px: 4,
          py: 6,
          background: panelColors.background,
          border: panelColors.border,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 150,
            height: 150,
            mx: "auto",
            mb: 3,
          }}
        >
          <Box
            component={motion.div}
            animate={{ rotate: 360 }}
            transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: `1px dashed ${panelColors.ring}`,
              opacity: 0.8,
            }}
          />
          <Box
            component={motion.div}
            animate={{ rotate: -360 }}
            transition={{ duration: 32, repeat: Infinity, ease: "linear" }}
            sx={{
              position: "absolute",
              inset: 14,
              borderRadius: "50%",
              border: `1px solid ${panelColors.ring}`,
              opacity: 0.35,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 46,
              borderRadius: "24px",
              backgroundColor: panelColors.iconBg,
              border: `1px solid ${panelColors.barFill}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: isDarkMode
                ? "0 12px 25px rgba(0,0,0,0.45)"
                : "0 12px 25px rgba(13,71,161,0.15)",
            }}
          >
            <CloudQueue sx={{ fontSize: 42, color: panelColors.barFill }} />
          </Box>
        </Box>
        <Typography
          variant="h5"
          sx={{ color: panelColors.textPrimary, fontWeight: 700, mb: 1.5 }}
        >
          {message}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: panelColors.textSecondary,
            maxWidth: 420,
            mx: "auto",
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          We're syncing up with Dropbox, confirming your project, and checking
          for any Supply folders. Hang tight, we'll take you straight in once the
          folders arrive.
        </Typography>
        <LinearProgress
          variant="indeterminate"
          sx={{
            height: 6,
            borderRadius: 999,
            backgroundColor: panelColors.barBackground,
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              background: panelColors.barFill,
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{ color: panelColors.textSecondary, display: "block", mt: 2 }}
        >
          Still working… thanks for your patience.
        </Typography>
      </Box>
    </Container>
  );
};

export default LoadingState;
